import * as HOOKS from '@siloreal:shared:hooks';
import { QueryKey } from '@tanstack/react-query';
import { forwardRef, ReactElement, RefAttributes, useCallback, useEffect } from 'react';
import {
  selector as FormSelector,
  SelectorOption,
  Props as FormSelectorProps,
} from '../form/field/selector-input';

type SelectorBaseProps<TOption extends SelectorOption> = Omit<
  Extract<FormSelectorProps<TOption>, { options: TOption[] }>,
  'options'
>;

type ServiceCallbacks<TResponse, TOption extends SelectorOption> = {
  onSuccess?: (options: TOption[]) => void;
  onError?: (error: unknown) => void;
  dataAccess?: (data: TResponse) => TOption[];
};

type ServiceConfigBase = {
  key: QueryKey;
};

type ServiceConfigWithoutAccess<TOption extends SelectorOption> = ServiceConfigBase & {
  fn: () => Promise<TOption[]>;
  callback?: Omit<ServiceCallbacks<TOption[], TOption>, 'dataAccess'> & {
    dataAccess?: never;
  };
};

type ServiceConfigWithAccess<TResponse, TOption extends SelectorOption> = ServiceConfigBase & {
  fn: () => Promise<TResponse>;
  callback: ServiceCallbacks<TResponse, TOption> & {
    dataAccess: (data: TResponse) => TOption[];
  };
};

type ServiceConfig<TOption extends SelectorOption, TResponse> =
  | ServiceConfigWithoutAccess<TOption>
  | ServiceConfigWithAccess<TResponse, TOption>;

export type Props<
  TOption extends SelectorOption = SelectorOption,
  TResponse = TOption[],
> = SelectorBaseProps<TOption> & {
  service: ServiceConfig<TOption, TResponse>;
};

const defaultDataAccess = <TOption extends SelectorOption>(data: TOption[]): TOption[] => data;

const serviceSelector = forwardRef(
  <TOption extends SelectorOption = SelectorOption, TResponse = TOption[]>(
    props: Props<TOption, TResponse>,
    ref: RefAttributes<HTMLSelectElement>['ref']
  ) => {
    const { service, ...selectorProps } = props;
    const dataAccess = service.callback?.dataAccess ?? defaultDataAccess;

    const selectOptions = useCallback(
      (data: TResponse | TOption[]): TOption[] => dataAccess(data as TResponse & TOption[]),
      [dataAccess]
    );

    const optionsQuery = HOOKS.useSiloRealService.query<TOption[], Error, TResponse | TOption[], QueryKey>(
      service.fn as () => Promise<TResponse | TOption[]>,
      {
        queryKey: service.key,
        select: selectOptions,
      }
    );

    useEffect(() => {
      if (optionsQuery.isSuccess) service.callback?.onSuccess?.(optionsQuery.data ?? []);
      if (optionsQuery.isError) service.callback?.onError?.(optionsQuery.error);
    }, [
      optionsQuery.data,
      optionsQuery.error,
      optionsQuery.isError,
      optionsQuery.isSuccess,
      service.callback,
    ]);

    return <FormSelector<TOption> {...selectorProps} options={optionsQuery.data ?? []} ref={ref} />;
  }
);

serviceSelector.displayName = 'SiloReal-Collection-ServiceSelector';

export const ServiceSelector = serviceSelector as <
  TOption extends SelectorOption = SelectorOption,
  TResponse = TOption[],
>(
  props: Props<TOption, TResponse> & { ref?: RefAttributes<HTMLSelectElement>['ref'] }
) => ReactElement;
